import * as angular from "angular"
import { getInfiltrationScore } from "../../hsa/infiltration"
import { IHealthySoilsAssessment, IHsaDrainage, IHsaDrillType, IHsaPrimaryCultivations } from "../../hsa/types"
import { convertGeometryToOpenLayers, defineComponent, doclib, FieldViewModel, IDocumentLibraryArguments, IDocumentLibraryService, ImportDocument, IOmniaSession, IOmniaShell, isNumber, ISoilAnalysis, ITab, numericTextSort, OmniaObjectType, shell } from "../../omnia"
import { ISoilService } from "../../types"
import { getActivity } from "../activity"
import { HsaPictureVM } from "./picture"
import { ol_proj, ol_geom } from "@omnia/exports/openlayers"

export class HsaEditPopupCtrl extends shell.Dialog {

    static create(): ng.IDirective {

        return defineComponent(HsaEditPopupCtrl, "app/soil/edit/hsa/edit.html", {
            context: "="
        });

    };

    public currentTab: ITab;
    public tabs: ITab[];

    private create: boolean;

    vessScores: string[] = ['1', '2', '3', '4', '5'];
    public assessment: IHealthySoilsAssessment;

    private allocTargets: FieldViewModel[];
    private selectedAllocTargets: FieldViewModel[];
    private deletedAllocTargets: FieldViewModel[];

    private currentAllocSelection: FieldViewModel;

    private hsaDrainages: IHsaDrainage[];
    private primaryCultivations: IHsaPrimaryCultivations[];
    private drillTypes: IHsaDrillType[];

    private hsaDrainage: IHsaDrainage;
    private primaryCultivation: IHsaPrimaryCultivations;
    private drillType: IHsaDrillType;

    private soilAnalysis: ISoilAnalysis;

    private _totalWorms: number;
    private _analysisNumber: string;

    private coordinates: number[];

    private pictures: HsaPictureVM[];

    static $inject = ["$scope", "OmniaSoilService", "OmniaSession", "OmniaShell", "OmniaDocumentLibraryService", "Lightbox"];

        constructor(
            $scope: ng.IScope,
            protected svc: ISoilService,
            private session: IOmniaSession,
            shell: IOmniaShell,
            private documentSvc: IDocumentLibraryService,
            private lightbox: any,
            readonly readOnly = shell.isReadOnly(p => p.soil)) {

        super($scope, shell)

    }

    $onInit() {

        this.setupTabs();

        this.hsaDrainages = this.svc.getHsaDrainage();
        this.primaryCultivations = this.svc.getPrimaryCultivations();
        this.drillTypes = this.svc.getDrillTypes();

        this.assessment = this.arguments;
        this.setupAssessment(this.assessment);

        const hsa = this.assessment
        this.setActivity(getActivity(this.create, { type: OmniaObjectType.HsAnlaysis, id: hsa?.HealthySoilsAssessmentId, number: hsa?.AnalysisNumber }))

    }

    get title(): string {
        return this.create ? 'Create a new healthy soils assessment' : 'Update a healthy soils assessment';
    }

    get hasWormsData(): boolean {
        // 180202: Epigeic worms removed for now by request from Oliver
        //return !!this.assessment.Anecic || !!this.assessment.Endogeic || !!this.assessment.Epigeic;
        return !!this.assessment.Anecic || !!this.assessment.Endogeic
    }

    get hasTotalWorms(): boolean {
        return !!this._totalWorms;
    }

    get totalWorms(): number {

        if (this._totalWorms) return this._totalWorms;

        let a = this.assessment;

        if (a && (isNumber(a.Anecic) || isNumber(a.Endogeic) || isNumber(a.Epigeic))) {
            // 180202: Epigeic worms removed for now by request from Oliver
            //return (a.Anecic || 0) + (a.Endogeic || 0) + (a.Epigeic || 0);
            return (a.Anecic || 0) + (a.Endogeic || 0)
        }

    }

    set totalWorms(value: number) {
        this._totalWorms = value;
    }

    get analysisNumber(): string {
        if (this._analysisNumber) return this._analysisNumber;
        return this.selectedAllocTargets.length === 1 ? this.selectedAllocTargets[0].fieldName : null;
    }

    set analysisNumber(value: string) {
        this._analysisNumber = value;
    }

    get topsoil(): string {
        if (this.selectedAllocTargets && this.selectedAllocTargets.length === 1) {
            return this.selectedAllocTargets[0].field.SoilType ? this.selectedAllocTargets[0].field.SoilType.Name : "Unknown top soil";
        }
        return "No field allocated";
    }

    get subsoil(): string {
        if (this.selectedAllocTargets && this.selectedAllocTargets.length === 1) {
            return this.selectedAllocTargets[0].field.SubsoilType ? this.selectedAllocTargets[0].field.SubsoilType.Name : "Unknown subsoil";
        }
        return "No field allocated";
    }

    private setupTabs() {

        this.tabs = [

            { key: "struct", text: "Structure" },
            { key: "fauna", text: "Fauna" },
            { key: "pH", text: "pH" },
            { key: "soilprop", text: "Properties" },
            { key: "other", text: "Other" },
            { key: "comments", text: "Comments" },
            { key: "pictures", text: "Pictures" }

        ];

        this.currentTab = this.tabs[0];

    }

    protected setupAssessment(assessment: IHealthySoilsAssessment) {

        this.assessment = angular.extend({}, assessment);

        this.pictures = this.assessment.Pictures ? this.assessment.Pictures.map(p => HsaPictureVM.create(p)) : [];

        this.hsaDrainage = this.assessment.DrainageId
            ? this.hsaDrainages.find(s => s.HsaDrainageId === this.assessment.DrainageId)
            : null;

        this.primaryCultivation = this.assessment.PrimaryCultivationsId
            ? this.primaryCultivations.find(s => s.HsaPrimaryCultivationsId === this.assessment.PrimaryCultivationsId)
            : null;

        this.drillType = this.assessment.DrillTypeId
            ? this.drillTypes.find(s => s.HsaDrillTypeId === this.assessment.DrillTypeId)
            : null;

        this._analysisNumber = this.assessment.AnalysisNumber;

        this._totalWorms = !this.assessment.Anecic &&
            !this.assessment.Endogeic &&
            this.assessment.TotalWorms
            ? this.assessment.TotalWorms
            : undefined;

        this.allocTargets = this.svc.fields
        this.allocTargets = numericTextSort(this.allocTargets, t => t.description);
        this.selectedAllocTargets = this.assessment.Allocations && this.assessment.Allocations.length
            ? this.allocTargets.filter(f => this.assessment.Allocations.filter(a => a.FieldId === f.fieldId)
                .length >
                0)
            : [];
        this.deletedAllocTargets = [];

        var analysis = this.selectedAllocTargets.length
            ? this.svc
                .getFieldSoilAnalyses(this.selectedAllocTargets[0].fieldId, false)
                .sort((a, b) => a.SamplingDate > b.SamplingDate ? -1 : a.SamplingDate < b.SamplingDate ? 1 : 0)
            : [];

        this.soilAnalysis = analysis.length ? analysis[0] : null;

        this.create = !assessment.HealthySoilsAssessmentId;

        if (this.create) {
            this.assessment.InfiltrationTapeWidth = 18;

            if (this.selectedAllocTargets.length === 1) {

                var geom = this.selectedAllocTargets[0].getBoundary();

                if (geom) {
                    const geometry = convertGeometryToOpenLayers(geom)
                    if (geometry instanceof ol_geom.Polygon) {
                        var point = geometry.getInteriorPoint();
                        var lonlat = ol_proj.transform(point.getCoordinates(), 'EPSG:3857', 'EPSG:4326');
                        this.assessment.LocationLong = lonlat[0];
                        this.assessment.LocationLat = lonlat[1];
                    }

                }

            }

        }
    }

    get hsa() {
        return this.assessment;
    }

    canLocate(): boolean {
        return !!navigator.geolocation;
    }

    locateUser() {
        navigator.geolocation.getCurrentPosition((pos) => {
            this.assessment.LocationLong = pos.coords.longitude;
            this.assessment.LocationLat = pos.coords.latitude;
            this.$scope.$apply();
        });
    }

    selectField(field: FieldViewModel) {
        if (field) {
            this.currentAllocSelection = null;

            if (this.selectedAllocTargets.indexOf(field) === -1 && this.deletedAllocTargets.indexOf(field) === -1) {
                this.selectedAllocTargets.push(field);
            }
        }
    }

    unselectField(field: FieldViewModel) {
        if (field && this.selectedAllocTargets.indexOf(field) !== -1) {

            if (this.assessment.Allocations && this.assessment.Allocations.filter(a => a.FieldId === field.fieldId).length > 0) {
                if (this.deletedAllocTargets.indexOf(field) === -1) {
                    this.deletedAllocTargets.push(field);
                }
            }

            this.selectedAllocTargets = this.selectedAllocTargets.filter(f => field !== f);
        }
    }

    recoverField(field: FieldViewModel) {
        if (field && this.deletedAllocTargets.indexOf(field) !== -1) {
            this.deletedAllocTargets = this.deletedAllocTargets.filter(f => field !== f);

            if (this.selectedAllocTargets.indexOf(field) === -1) {
                this.selectedAllocTargets.push(field);
            }
        }
    }

    changeLocation() {

        this.shell.showComponentAsDialog("omnia-soil-assessment-location-popup", this.assessment, {
            backdrop: "static",
            size: "lg"
        })

    }

    get infiltrationRateValue(): number {
        if (!this.assessment.InfiltrationTapeWidth || !this.assessment.InfiltrationTime) return null;
        return Math.round(this.assessment.InfiltrationTapeWidth / (this.assessment.InfiltrationTime / 3600));
    }

    get infiltrationRateText(): string {
        var val = this.infiltrationRateValue;
        var res = getInfiltrationScore(val);

        if (val) {
            res = res + " (" + this.infiltrationRateValue + " mm/hr)";
        }

        return res;
    }

    updateYieldPotentialPct() {

        var p = this.assessment.YieldPotential;

        if (p) {
            this.assessment.YieldPotentialPct = this.assessment.YieldAverage / p;
        }

    }

    canMoveDown(picture: HsaPictureVM) {

        return picture.index < this.pictures.length - 1;

    }

    moveDown(picture: HsaPictureVM) {

        var other = this.pictures.find(p => p.index === picture.index + 1);
        other.index = picture.index;
        picture.index = picture.index + 1;

    }

    moveUp(picture: HsaPictureVM) {

        var other = this.pictures.find(p => p.index === picture.index - 1);
        other.index = picture.index;
        picture.index = picture.index - 1;

    }

    removePicture(picture: HsaPictureVM) {

        this.pictures.remove(p => p === picture);

        var sortedPictures = this.pictures.sort((a, b) => a.index > b.index ? 1 : a.index < b.index ? -1 : 0);

        for (var i = 0; i < sortedPictures.length; i++) {
            sortedPictures[i].index = i;
        }

    }

    canMoveUp(picture: HsaPictureVM) {

        return picture.index > 0;

    }

    showLightbox(picture: ImportDocument) {
        var path = this.getImgPath(picture);
        this.lightbox.openModal([{ url: path }], 0);
    }

    addPicture() {

        const args: IDocumentLibraryArguments = {
            selection: true,
            limitToPictures: true,
            multipleSelection: true
        }

        this.shell.showComponentAsDialog(doclib.ReactDocumentLibrary, args,
            {
                size: 'full-screen',
                backdrop: 'static'
            }).then(r => {
                if (r && r.length) {
                    r.forEach(d => this.pictures.push(HsaPictureVM.createNew(d, this.pictures.length, this.assessment.HealthySoilsAssessmentId)));
                }
            });

    }

    private getImgPath(document: ImportDocument) {
        return this.documentSvc.getImgPath(document);
    }

    canCommit() {
        if (!this.form?.$valid) return false
        if (this.readOnly) return false
        return true
    }

    commit(): void {

        if (!this.canCommit()) {
            this.showValidationErrors = true;
            return;
        }

        this.showValidationErrors = false;

        const hsa: IHealthySoilsAssessment = angular.extend({}, this.assessment);

        hsa.HoldingId = this.session.holdingId;
        hsa.InfiltrationRate = this.infiltrationRateValue;
        hsa.DrainageId = !!this.hsaDrainage ? this.hsaDrainage.HsaDrainageId : null;
        hsa.PrimaryCultivationsId = !!this.primaryCultivation ? this.primaryCultivation.HsaPrimaryCultivationsId : null;
        hsa.DrillTypeId = !!this.drillType ? this.drillType.HsaDrillTypeId : null;
        hsa.TotalWorms = this.totalWorms;
        hsa.AnalysisNumber = this.analysisNumber;

        hsa.Pictures = this.pictures.map(p => p.getModel());

        hsa.Drainage = null;
        hsa.PrimaryCultivations = null;
        hsa.DrillType = null;
        hsa.Series = null;

        var location = [];

        if (hsa.LocationLong && hsa.LocationLat) {
            location = ol_proj.transform([hsa.LocationLong, hsa.LocationLat], 'EPSG:4326', 'EPSG:3857');
        }

        let allocFieldsIds = this.selectedAllocTargets && this.selectedAllocTargets.length > 0 ? this.selectedAllocTargets.map(f => f.fieldId) : null;

        this.svc.saveHsa(hsa, allocFieldsIds, location)
            .then(() => this.closeComponent(true));

    }

    getWormsByM3(value: number) {
        if (value) {
            return value * 16;
        }
    }

    getWormsByHa(value: number) {
        if (value) {
            return (value * 16) * 10000;
        }
    }

    canDelete() {
        return this.hsa && (!this.create) && (!this.readOnly)
    }

    delete() {

        var content = "<p>Click 'Delete' to delete this assessment, and all its current allocations.</p><p>Please note that this operation is irreversible.</p>";

        var p = shell.showDeleteCancel(this.shell, content, "Delete this assessment");

        p.then(r => {

            if (r) {

                this.svc.deleteHsa(this.hsa.HealthySoilsAssessmentId)
                    .then(() => this.closeComponent(-1));

            }

        });

    }


}
